import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Button,
  Paper,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  Alert,
} from '@mui/material';
import {
  Work as JobIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const CreatePost = () => {
  const [postType, setPostType] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { hasCapability } = useAuth();

  const handlePostTypeChange = (event) => {
    setPostType(event.target.value);
    setError('');
  };

  const handleContinue = () => {
    if (!postType) {
      setError('Please select what you want to post');
      return;
    }

    if (postType === 'job') {
      // Check if user has job posting capabilities
      if (hasCapability('canPostJobs')) {
        navigate('/employer/post-job');
      } else {
        // Redirect to employer wizard to complete missing information
        navigate('/wizard/employer', { 
          state: { 
            returnTo: '/employer/post-job',
            message: 'Complete your employer profile to post jobs',
            targetRole: 'employer'
          }
        });
      }
    }

  };

  const getPostingOptions = () => {
    const options = [];

    // Job posting option
    options.push({
      value: 'job',
      title: 'Post a Job',
      description: 'Hire talented professionals for your company',
      icon: <JobIcon sx={{ fontSize: 48, color: 'primary.main' }} />,
      available: true,
      requiresWizard: !hasCapability('canPostJobs')
    });

    return options;
  };

  const postingOptions = getPostingOptions();

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4, textAlign: 'center' }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Create a New Post
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Choose what you want to post on our platform
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 4 }}>
          <FormControl component="fieldset" fullWidth>
            <FormLabel component="legend" sx={{ mb: 3, fontSize: '1.1rem', fontWeight: 'medium' }}>
              What would you like to post?
            </FormLabel>
            
            <RadioGroup
              value={postType}
              onChange={handlePostTypeChange}
              sx={{ gap: 2 }}
            >
              <Grid container spacing={3}>
                {postingOptions.map((option) => (
                  <Grid item xs={12} md={6} key={option.value}>
                    <Paper
                      elevation={postType === option.value ? 3 : 1}
                      sx={{
                        p: 3,
                        cursor: 'pointer',
                        border: postType === option.value ? 2 : 1,
                        borderColor: postType === option.value ? 'primary.main' : 'grey.300',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          elevation: 2,
                          borderColor: 'primary.main',
                        },
                      }}
                      onClick={() => setPostType(option.value)}
                    >
                      <FormControlLabel
                        value={option.value}
                        control={<Radio sx={{ display: 'none' }} />}
                        label={
                          <Box sx={{ textAlign: 'center', width: '100%' }}>
                            <Box sx={{ mb: 2 }}>
                              {option.icon}
                            </Box>
                            <Typography variant="h6" component="h3" gutterBottom>
                              {option.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                              {option.description}
                            </Typography>
                            {option.requiresWizard && (
                              <Alert severity="info" sx={{ mt: 2, textAlign: 'left' }}>
                                <Typography variant="caption">
                                  Complete your profile setup to unlock this feature
                                </Typography>
                              </Alert>
                            )}
                          </Box>
                        }
                        sx={{ 
                          m: 0, 
                          width: '100%',
                          '& .MuiFormControlLabel-label': {
                            width: '100%'
                          }
                        }}
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>
          </FormControl>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleContinue}
              disabled={!postType}
              endIcon={<ArrowForwardIcon />}
              sx={{ minWidth: 200 }}
            >
              Continue
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Information Section */}
      <Box sx={{ mt: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Job Posting
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Post job openings once you complete your employer profile.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Complete the employer wizard to unlock job posting features.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default CreatePost;